import { ConditionalLogic, FormContext } from "../types";
import React, { useState } from "react";
import { ArrowLeftIcon } from "@boi/react-icons";

import Actions from "./actions";
import { evaluates_as_hiddenV2 } from "../conditional-logic";

type SubmitCallback = {
    (): void;
};

type ButtonProps = {
    text: string;
    conditionalLogic?: ConditionalLogic;
};

interface Props {
    context: FormContext;
    nextButton?: ButtonProps;
    previousButton?: ButtonProps;
    hideNext?: boolean;
    hideBack?: boolean;
    onSubmit?: SubmitCallback;
    cssClass?: string;
}

interface SubmitProps extends Props {
    submitting: boolean;
    setSubmitting: (submitting: boolean) => void;
}

const createSubmitButton = ({ context, ...props }: SubmitProps) => {
    const { form, state } = context;
    return [
        {
            text: form.button?.text || "Submit",
            "aria-label": form.button?.text || "Submit the form",
            variant: "primary",
            disabled:
                props.submitting ||
                !!Object.entries(state.validation).length ||
                evaluates_as_hiddenV2(form.button?.conditionalLogic, state),
            onClick: () => {
                props.setSubmitting(true);
                props.onSubmit && props.onSubmit();
            }
        }
    ];
};

const createNextButton = ({ context, ...props }: Props) => {
    let ariaLabel = props.nextButton && props.nextButton.text;
    if (!ariaLabel || ariaLabel === "Next" || ariaLabel === "Continue") {
        ariaLabel = "Continue to next page of the form";
    } else {
        ariaLabel = `${ariaLabel}, continue to next page of the form`;
    }

    return [
        {
            text: (props.nextButton && props.nextButton.text) || "Next",
            "aria-label": ariaLabel,
            variant: "primary",
            disabled: context.isNextDisabled(),
            onClick: () => context.gotoNextPage()
        }
    ];
};

const addSecondaryButton = ({ context, ...props }: Props) => {
    let ariaLabel = props.previousButton && props.previousButton.text;
    if (!ariaLabel || ariaLabel === "Back") {
        ariaLabel = "Back to previous page of the form";
    } else {
        ariaLabel = `${ariaLabel}, back to previous page of the form`;
    }

    return context.state.page > 1
        ? [
              {
                  text:
                      (props.previousButton && props.previousButton.text) ||
                      "Back",
                  "aria-label": ariaLabel,
                  variant: "secondary",
                  startIcon: <ArrowLeftIcon />,
                  onClick: () => context.gotoPreviousPage()
              }
          ]
        : [];
};

const addPrimaryButton = (props: SubmitProps) =>
    props.context.pageIsLast()
        ? !props.context.form.button?.hide
            ? createSubmitButton(props)
            : []
        : createNextButton(props);

const addForwardButton = ({ context }: Props) =>
    ((context.state.response && !context.state.response.isValid) ||
        (context.state.visitedLastPage &&
            !Object.entries(context.state.validation).length)) &&
    !context.pageIsLast()
        ? [
              {
                  text: "Back to last page",
                  variant: "forward",
                  disabled: context.isNextDisabled(),
                  onClick: () => context.gotoLastPage(true)
              }
          ]
        : [];

const FormActions = (props: Props) => {
    const [submitting, setSubmitting] = useState(false);
    const buttons = [
        ...(!props.hideNext
            ? addPrimaryButton({ ...props, submitting, setSubmitting })
            : []),
        ...(!props.hideBack ? addSecondaryButton(props) : [])
    ];
    return props.hideNext && props.hideBack ? (
        <></>
    ) : (
        <>
            <Actions
                cssClass={`form-navigation ${props.cssClass}`}
                buttons={buttons}
            />
            <Actions
                cssClass={`form-navigation u-pt-0`}
                buttons={addForwardButton(props)}
            />
        </>
    );
};

export default FormActions;
